import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { removeOldestQuery } from "@tanstack/react-query-persist-client";
import type { Query } from "@tanstack/react-query";

import { frontendBuild, UNKNOWN } from "@/lib/buildInfo";

/**
 * Tracker cache persistence (Phase 0 + 1 of the user-ready caching plan).
 *
 * Persists the TanStack Query cache to ``window.localStorage`` so that
 * returning users see ``/portal`` and any previously-visited
 * ``/portal/buildings/<id>`` paint instantly — no spinner, no waiting
 * for the BE round-trip. Fresh data still arrives in the background
 * (stale-while-revalidate); the cache hit is what eliminates the
 * user-felt latency.
 *
 * Why localStorage (not IndexedDB):
 *   - Realistic Tracker portfolio sizes are <200 buildings → <500KB
 *     cached, comfortably under the 5MB localStorage cap.
 *   - sync persister + localStorage means no async glue, no
 *     extra deps, no IndexedDB schema/version events to maintain.
 *   - Persister throttles writes (default 1s) so the sync-blocking
 *     cost is bounded to ~10-30ms at most once per second of active
 *     mutation.
 *   - If a customer ever brings 1000+ buildings and hits the cap, the
 *     persister-interface contract is the same: swap to
 *     ``createAsyncStoragePersister`` + ``idb-keyval`` in ~10 LOC.
 */

/**
 * Leo 10-25: the build sha busts the cache on every deploy (`CACHE_BUSTER`).
 * Bump this by hand only for a local-only shape change, where the sha is
 * `unknown`. Bump history through v31: `git log -L` on this line.
 */
export const CACHE_SCHEMA_VERSION = "2026-10-22-v31";

/** `CACHE_SCHEMA_VERSION:<sha>`; the constant alone where the sha is unknown. */
export const cacheBuster = (gitSha: string): string =>
  gitSha && gitSha !== UNKNOWN
    ? `${CACHE_SCHEMA_VERSION}:${gitSha}`
    : CACHE_SCHEMA_VERSION;

export const CACHE_BUSTER = cacheBuster(frontendBuild.gitSha);

const STORAGE_KEY = "cc-tanstack-query-cache";

/**
 * Queries we MUST NOT persist:
 *   - Auth/user identity (handled by the token in localStorage already
 *     and refreshed by ``loadUser``).
 *   - Warmup status (active polling; persisting a stale "still
 *     warming" state would block "Add Building" completion banners).
 *   - Any query that errored — persisting errors is misleading.
 *   - Queries the consumer explicitly tagged with ``meta.persist:
 *     false`` (escape hatch).
 *
 * Default: persist everything else. The L2 hit is what makes the
 * dashboard + building detail feel instant on return.
 */
const NEVER_PERSIST_KEY_PREFIXES: ReadonlyArray<string> = [
  "auth",
  "user",
  "me",
  "warmup-status",
];

export const shouldPersistQuery = (query: Query): boolean => {
  if (query.state.status !== "success") return false;
  if (query.meta?.persist === false) return false;

  const firstKey = query.queryKey[0];
  if (typeof firstKey !== "string") return true;
  return !NEVER_PERSIST_KEY_PREFIXES.includes(firstKey);
};

/**
 * Build the persister. Caller wires this to
 * ``PersistQueryClientProvider`` in ``app.tsx``.
 *
 * ── QUOTA USED TO KILL THE CACHE SILENTLY ─────────────────────────────────
 *
 * With no ``retry``, ``query-sync-storage-persister``'s ``trySave`` does
 * ``client = retry?.({…})`` → ``undefined`` → the while loop exits and the
 * ``QuotaExceededError`` is DISCARDED. No log, no eviction, no signal: writes
 * stop for the rest of the session, the last pre-overflow blob stays in
 * storage, and every later load rehydrates it. The L2 cache is dead and
 * serving stale data with nothing anywhere saying so — which is the exact
 * failure the ``CACHE_BUSTER`` above exists to prevent, and
 * it was reachable from below the version gate.
 *
 * ``removeOldestQuery`` (shipped by ``@tanstack/react-query-persist-client``)
 * drops the least-recently-updated query and RETRIES, so an over-budget cache
 * degrades oldest-first instead of dying whole.
 *
 * WHAT MADE IT REACHABLE. ``BUILDINGS_PAGE_SIZE`` went 10 → 50: one persisted
 * buildings entry is ~1.5 KB/row with PR-8's ``attribution_split`` +
 * ``unresolved_90d_plus``, so ~75 KB per page against ~15 KB, and there is one
 * entry per (page, q, sortBy, sortDir). The per-building ``/codes`` payloads
 * the hover prefetch pulls are ~18 KB each; those no longer persist at all
 * (``hooks/useRowHoverPrefetch.ts`` tags them ``meta.persist: false`` — a
 * guess should not spend the budget), but the buildings entries still do, and
 * the budget is ~5 MB per origin.
 */
export const createTrackerQueryPersister = () =>
  createSyncStoragePersister({
    storage: typeof window === "undefined" ? undefined : window.localStorage,
    key: STORAGE_KEY,
    // Throttle storage writes to once per second to bound the sync
    // localStorage write cost. Default is 1000ms; making it explicit
    // here so the contract is auditable.
    throttleTime: 1000,
    // See the docblock: without this, a quota overflow is swallowed and
    // persistence stops for the session with no signal at all.
    retry: removeOldestQuery,
  });

/**
 * Wipe the persisted cache. Called on:
 *   - logout (``api/auth.tsx``)
 *   - enter impersonation (``components/common/ImpersonationMenu.tsx``)
 *   - exit impersonation (same file)
 *
 * Compliance data in localStorage MUST NOT survive session boundaries.
 * The next user (or the same user post-logout) sees a cold cache, not
 * the prior session's cached state.
 *
 * Best-effort: localStorage may be unavailable (Safari private mode,
 * disabled by user). Silently no-op in that case — we already don't
 * have anything to wipe.
 */
export const wipeQueryCache = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // No-op. Storage unavailable.
  }
};
